/**
 * Crucible #1945 — translate the cloud runtime's `dev.start@1`
 * streaming chunks into the cookie-mode preview contract the shared
 * Preview overlay consumes: the `CloudPreviewDevSession` returned by
 * `invoke('dev:start')` and the session-scoped `preview:cloud-update`
 * push events.
 *
 * `BrowserCrucibleClient.invoke('dev:start', …)` routes the launch over
 * `invokeStreaming` (the chunks are `dev.start.chunk@1` notifications
 * correlated by `params.requestId`), then bridges the stream through
 * these helpers so `usePreviewSession.start()` resolves with the cloud
 * session shape it awaits — WITHOUT the call site changing. This is the
 * `dev:start` twin of the `game:create` (#1314) and `agent:send`
 * (#1777) bridges.
 *
 * Why a bridge is REQUIRED: `dev.start@1` is a streaming method, but in
 * cookie mode `dev:start` was neither runtime-bridged nor stubbed, so
 * `invoke('dev:start')` fell through to a BARE `sendRequest`. The
 * request-correlated `dev.start.chunk@1` chunks then had no registered
 * streaming handler (`stream chunk for unknown requestId — dropping`)
 * and the bare request never resolved → the preview overlay hung on its
 * placeholder forever (the #1932 furnace-preview spinner).
 *
 * The consume sequence mirrors the desktop orchestrator
 * (`crucible-gui/src/main/cloud-preview-launch.ts`), minus the
 * main-process concerns the browser doesn't have (pre-flight auth — the
 * browser already carries the FA cookie — the session registry, and
 * dev-status persistence):
 *
 *   FIRST `preview-url{state:ready|restart, externalUrl}` chunk
 *     → adopt: resolve a `{runtime:'cloud', sessionId, gameId, previewUrl}`
 *   later differing externalUrl → push `url-swap`
 *   terminal (stream done / error / end) → push `closed`
 *
 * **Version skew is loud (TDD D4 / crucible#1478):** a `preview-url`
 * chunk WITHOUT `externalUrl` means the pod image predates the
 * externalUrl wire field or `FOUNDRY_PREVIEW_BASE_URL` is missing on the
 * pod (bifrost#122). The pod-local `url` is unreachable from the browser,
 * so adopting it would iframe an unreachable host and present as the same
 * infinite spinner — the launcher throws instead.
 */

import type { CloudPreviewDevSession, CloudPreviewPushEvent } from '@volley/crucible-ipc-types';

/**
 * Budget for the `dev:start` bridge to receive the first ready
 * `preview-url` chunk before it rejects. Mirrors the desktop's
 * `FIRST_URL_TIMEOUT_MS` (`cloud-preview-launch.ts`): a cold pod
 * (`pnpm install`) can take minutes, so this is generous — but BOUNDED,
 * so a stalled / log-only stream rejects rather than hanging the preview
 * overlay forever (Codex P2 on #1948). Overridable per-client via
 * `BrowserCrucibleClientOptions.devStartFirstUrlTimeoutMs` (tests inject a
 * short value).
 */
export const DEV_START_FIRST_URL_TIMEOUT_MS = 180_000;

/**
 * Mirror of the runtime's originator wire chunk for `dev.start@1` (the
 * `dev.start.chunk@1` notification `params`, minus the `requestId`/`seq`
 * envelope fields the core strips). Re-declared here because
 * `@volley/crucible-client` does not depend on `@volley/crucible` (the
 * runtime package that owns the chunk type); the streaming
 * notifications arrive as `params` objects of this shape.
 *
 * `chunkKind` ∈ `log` | `error` | `preview-url` | `done`. `externalUrl`
 * rides on `preview-url` chunks when the pod's Furnace controller
 * injected `FOUNDRY_PREVIEW_BASE_URL` (crucible#1478 / bifrost#122); the
 * pod-local `url` is unreachable from the browser.
 */
export interface DevStartWireChunk {
  chunkKind: string;
  line?: string;
  url?: string;
  externalUrl?: string;
  state?: string;
  gameId?: string;
}

/**
 * A `preview-url` chunk is a "ready" signal when its `state` is `ready`
 * or `restart` (the two states the driver emits a URL on). Other
 * chunkKinds (`log`, `error`, `done`) and other states are not URL
 * signals.
 */
export function isReadyPreviewUrlChunk(chunk: DevStartWireChunk): boolean {
  return (
    chunk.chunkKind === 'preview-url' && (chunk.state === 'ready' || chunk.state === 'restart')
  );
}

/**
 * The external preview URL carried by a ready `preview-url` chunk, or
 * `null` when the chunk isn't a ready URL signal OR carries no non-empty
 * `externalUrl` (the version-skew case — see the module docblock). Never
 * falls back to the pod-local `url`: it is unreachable from the browser.
 */
export function previewUrlFromChunk(chunk: DevStartWireChunk): string | null {
  if (!isReadyPreviewUrlChunk(chunk)) return null;
  return typeof chunk.externalUrl === 'string' && chunk.externalUrl.length > 0
    ? chunk.externalUrl
    : null;
}

/** Build the cloud session shape `invoke('dev:start')` resolves with —
 *  the intentional cloud shape (`runtime:'cloud'`), NOT the local
 *  `DevSession`. The renderer narrows on `'runtime' in reply`. */
export function makeCloudPreviewSession(
  gameId: string,
  sessionId: string,
  previewUrl: string,
): CloudPreviewDevSession {
  return { runtime: 'cloud', sessionId, gameId, previewUrl };
}

/** A session-scoped `url-swap` push — the pod dev server restarted onto
 *  a new external URL. The renderer drops events whose `sessionId`
 *  differs from its adopted session. */
export function devStartUrlSwapEvent(
  gameId: string,
  sessionId: string,
  previewUrl: string,
): CloudPreviewPushEvent {
  return { kind: 'url-swap', gameId, sessionId, previewUrl };
}

/** A session-scoped `closed` push — the pod dev-server stream ended (or
 *  errored). The renderer drops the iframe back to the start
 *  affordance. */
export function devStartClosedEvent(
  gameId: string,
  sessionId: string,
  reason: 'ended' | 'error' = 'ended',
  message?: string,
): CloudPreviewPushEvent {
  return message === undefined
    ? { kind: 'closed', gameId, sessionId, reason }
    : { kind: 'closed', gameId, sessionId, reason, message };
}
